import { SUBSCRIPTION_ACTIONS } from "../actions/subscriptionActions";

const initialState = {
  // Package selection
  selectedPackage: null,
  userCount: 1,
  billingType: "monthly",

  // Discount codes
  discountCode: null,
  appliedDiscount: null,
  isValidatingDiscount: false,
  discountError: null,

  // Pricing calculations
  calculatedPricing: {
    subtotal: 0,
    discount: 0,
    total: 0,
    basePrice: 0,
    userCount: 1,
  },

  // Registration flow
  registrationData: null,
  isRegistrationFlow: false,

  // Subscription flow state
  flowStep: "package_selection", // package_selection, configuration, registration, payment, completed
  subscriptionResult: null,

  // UI state
  isLoading: false,
  error: null,

  // My Subscription Management
  currentSubscription: null,
  ownedSubscriptions: [],
  isFetchingSubscription: false,
  subscriptionError: null,

  // Billing data
  billingHistory: {
    subscription: null,
    subscriptions: [],
    paymentHistory: [],
  },
  isFetchingBilling: false,
  billingError: null,

  // Cancellation state
  isCancelling: false,

  // Subscription status check
  isCheckingStatus: false,
  hasActiveSubscription: null, // null = unknown, true = has active, false = no active
  subscriptionStatus: null,
  cancellationError: null,

  // Metadata
  lastUpdated: null,
  flowStartTime: null,
};

const subscriptionReducer = (state = initialState, action) => {
  switch (action.type) {
    // Package selection
    case SUBSCRIPTION_ACTIONS.SET_SELECTED_PACKAGE:
      return {
        ...state,
        selectedPackage: action.payload,
        lastUpdated: Date.now(),
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.SET_USER_COUNT:
      return {
        ...state,
        userCount: Math.max(1, action.payload),
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.SET_BILLING_TYPE:
      return {
        ...state,
        billingType: action.payload,
        lastUpdated: Date.now(),
      };

    // Discount codes
    case SUBSCRIPTION_ACTIONS.SET_DISCOUNT_CODE:
      return {
        ...state,
        discountCode: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.SET_APPLIED_DISCOUNT:
      return {
        ...state,
        appliedDiscount: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_START:
      return {
        ...state,
        isValidatingDiscount: true,
        discountError: null,
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_SUCCESS:
      return {
        ...state,
        isValidatingDiscount: false,
        appliedDiscount: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR:
      return {
        ...state,
        isValidatingDiscount: false,
        discountError: action.payload,
        appliedDiscount: null,
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_DISCOUNT:
      return {
        ...state,
        discountCode: null,
        appliedDiscount: null,
        discountError: null,
        isValidatingDiscount: false,
        lastUpdated: Date.now(),
      };

    // Pricing calculations
    case SUBSCRIPTION_ACTIONS.SET_CALCULATED_PRICING:
      return {
        ...state,
        calculatedPricing: {
          ...state.calculatedPricing,
          ...action.payload,
        },
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CALCULATE_PRICING:
      // This action is handled by the async action creator
      return state;

    // Registration flow
    case SUBSCRIPTION_ACTIONS.SET_REGISTRATION_DATA:
      return {
        ...state,
        registrationData: action.payload,
        isRegistrationFlow: true,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_REGISTRATION_DATA:
      return {
        ...state,
        registrationData: null,
        isRegistrationFlow: false,
        lastUpdated: Date.now(),
      };

    // Subscription flow
    case SUBSCRIPTION_ACTIONS.START_SUBSCRIPTION_FLOW:
      return {
        ...state,
        selectedPackage: action.payload,
        flowStep: "package_selection",
        flowStartTime: Date.now(),
        subscriptionResult: null,
        error: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.COMPLETE_SUBSCRIPTION_FLOW:
      return {
        ...state,
        flowStep: "completed",
        subscriptionResult: action.payload,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_SUBSCRIPTION_DATA:
      return {
        ...initialState,
        lastUpdated: Date.now(),
      };

    // UI state
    case SUBSCRIPTION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case SUBSCRIPTION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    // My Subscription Management
    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_START:
      return {
        ...state,
        isFetchingSubscription: true,
        subscriptionError: null,
      };

    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        isFetchingSubscription: false,
        currentSubscription: action.payload.currentSubscription,
        ownedSubscriptions: action.payload.subscriptions || [],
        subscriptionError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_ERROR:
      return {
        ...state,
        isFetchingSubscription: false,
        subscriptionError: action.payload,
        currentSubscription: null,
        ownedSubscriptions: [],
      };

    // Subscription Cancellation
    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_START:
      return {
        ...state,
        isCancelling: true,
        cancellationError: null,
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        isCancelling: false,
        currentSubscription: action.payload, // Updated subscription after cancellation
        cancellationError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        isCancelling: false,
        cancellationError: action.payload,
      };

    // Billing History
    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_START:
      return {
        ...state,
        isFetchingBilling: true,
        billingError: null,
      };

    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_SUCCESS:
      return {
        ...state,
        isFetchingBilling: false,
        billingHistory: action.payload,
        billingError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_ERROR:
      return {
        ...state,
        isFetchingBilling: false,
        billingError: action.payload,
      };

    // Subscription status check cases
    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_START:
      return {
        ...state,
        isCheckingStatus: true,
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS:
      return {
        ...state,
        isCheckingStatus: false,
        hasActiveSubscription: action.payload.hasSubscription,
        subscriptionStatus: action.payload.status,
        currentSubscription: action.payload.subscription,
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_ERROR:
      return {
        ...state,
        isCheckingStatus: false,
        hasActiveSubscription: false, // Assume no subscription on error
        subscriptionStatus: null,
        error: action.payload,
      };

    default:
      return state;
  }
};

// Selectors
const getSubscriptionState = (state) => state?.subscription || initialState;

export const selectSelectedPackage = (state) =>
  getSubscriptionState(state).selectedPackage;
export const selectUserCount = (state) => getSubscriptionState(state).userCount;
export const selectBillingType = (state) =>
  getSubscriptionState(state).billingType;
export const selectAppliedDiscount = (state) =>
  getSubscriptionState(state).appliedDiscount;
export const selectCalculatedPricing = (state) =>
  getSubscriptionState(state).calculatedPricing;
export const selectIsValidatingDiscount = (state) =>
  getSubscriptionState(state).isValidatingDiscount;
export const selectDiscountError = (state) =>
  getSubscriptionState(state).discountError;
export const selectRegistrationData = (state) =>
  getSubscriptionState(state).registrationData;
export const selectIsRegistrationFlow = (state) =>
  getSubscriptionState(state).isRegistrationFlow;
export const selectFlowStep = (state) => getSubscriptionState(state).flowStep;
export const selectSubscriptionError = (state) =>
  getSubscriptionState(state).error;
export const selectIsLoading = (state) => getSubscriptionState(state).isLoading;

// My Subscription selectors
export const selectCurrentSubscription = (state) =>
  getSubscriptionState(state).currentSubscription;
export const selectOwnedSubscriptions = (state) =>
  getSubscriptionState(state).ownedSubscriptions;
export const selectIsFetchingSubscription = (state) =>
  getSubscriptionState(state).isFetchingSubscription;
export const selectSubscriptionFetchError = (state) =>
  getSubscriptionState(state).subscriptionError;
export const selectIsCancelling = (state) =>
  getSubscriptionState(state).isCancelling;
export const selectCancellationError = (state) =>
  getSubscriptionState(state).cancellationError;

// Subscription status selectors
export const selectIsCheckingStatus = (state) =>
  getSubscriptionState(state).isCheckingStatus;
export const selectHasActiveSubscription = (state) =>
  getSubscriptionState(state).hasActiveSubscription;
export const selectSubscriptionStatus = (state) =>
  getSubscriptionState(state).subscriptionStatus;

// Billing selectors
export const selectBillingHistory = (state) =>
  getSubscriptionState(state).billingHistory;
export const selectIsFetchingBilling = (state) =>
  getSubscriptionState(state).isFetchingBilling;
export const selectBillingError = (state) =>
  getSubscriptionState(state).billingError;

// Complex selectors
export const selectIsPackageConfigured = (state) => {
  const subscription = getSubscriptionState(state);
  return !!(subscription.selectedPackage && subscription.userCount > 0);
};

export const selectCanProceedToPayment = (state) => {
  const subscription = getSubscriptionState(state);
  return !!(
    subscription.selectedPackage &&
    subscription.userCount > 0 &&
    subscription.calculatedPricing.total >= 0 &&
    !subscription.isValidatingDiscount
  );
};

export const selectVolumeDiscount = (state) => {
  const { selectedPackage, userCount } = getSubscriptionState(state);
  if (
    !selectedPackage?.volumeDiscounts ||
    selectedPackage.pricingModel !== "per_user"
  ) {
    return null;
  }

  return (
    selectedPackage.volumeDiscounts
      .filter((vd) => userCount >= vd.minUsers)
      .sort((a, b) => b.minUsers - a.minUsers)[0] || null
  );
};

export const selectTotalSavings = (state) => {
  const { calculatedPricing } = getSubscriptionState(state);
  const volumeDiscount = selectVolumeDiscount(state);

  let savings = calculatedPricing.discount || 0;

  // Add volume discount savings (this is already calculated in the pricing)
  // but we can show it separately for UI purposes
  if (volumeDiscount) {
    // Volume discount is already applied in calculatedPricing.subtotal
    // This selector is for display purposes
  }

  return savings;
};

export const selectSubscriptionSummary = (state) => {
  const subscription = getSubscriptionState(state);
  return {
    package: subscription.selectedPackage,
    userCount: subscription.userCount,
    billingType: subscription.billingType,
    pricing: subscription.calculatedPricing,
    discount: subscription.appliedDiscount,
    volumeDiscount: selectVolumeDiscount(state),
    canProceed: selectCanProceedToPayment(state),
    isConfigured: selectIsPackageConfigured(state),
  };
};

export default subscriptionReducer;
